import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ModalCloseX from "../components/common/ModalCloseX";
import InfoTooltip from "../components/common/InfoTooltip";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import { useAuth } from "../context/AuthContext";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import "./ReportsSectionCompact.css";
import {
  exportGiftCardsCashDrawerReport,
  getGiftCardsCashDrawerReport,
} from "../api/reports";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";
import { getSalonTimezone } from "../utils/salonTimezone";
import { GIFT_CARDS_CASH_DRAWER_TOOLTIPS } from "../constants/reportTooltips";
import { REPORT_HELP } from "../constants/reportHelpContent";
import {
  getReportPathForRoute,
  getReportsHomePathForRoute,
} from "../utils/reportRouteContext";

const MANAGER_ROLES = [
  "admin",
  "host",
  "owner",
  "manager",
  "solo_technician",
  "reception",
];

const VALID_GROUP_BY = new Set(["day", "week"]);

const REASON_LABELS = {
  cash_over_short_variance: "Large cash over/short variance",
  paid_in_spike: "Paid-in spike vs baseline",
  paid_out_spike: "Paid-out spike vs baseline",
};

const formatDateISO = (value, timeZone) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(value);
    } catch (err) {
      // Fall back to local time when the timezone is invalid or unsupported.
    }
  }
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (baseDate, deltaDays) => {
  const next = new Date(baseDate.getTime());
  next.setDate(next.getDate() + deltaDays);
  return next;
};

const formatMoney = (value) => {
  const amount = Number(value ?? 0);
  if (!Number.isFinite(amount)) return "$0.00";
  return `$${amount.toFixed(2)}`;
};

const formatNumber = (value, digits = 0) => {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) {
    return digits > 0 ? (0).toFixed(digits) : "0";
  }
  return num.toFixed(digits);
};

const formatDisplayDate = (value) => {
  if (!value) return "";
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return `${raw.slice(5, 7)}/${raw.slice(8, 10)}/${raw.slice(0, 4)}`;
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

export default function GiftCardsCashDrawerReport() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentSalon, user, loading: authLoading, role: ctxRole } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const helpGuide = REPORT_HELP.GIFT_CARDS_CASH_DRAWER;

  const role = useMemo(
    () => (ctxRole || localStorage.getItem("role") || "").toLowerCase(),
    [ctxRole]
  );

  const resolvedSalonId = useMemo(() => {
    const raw = currentSalon?.id ?? currentSalon?.salon_id ?? user?.salon_id ?? null;
    if (raw === null || raw === undefined || raw === "") return null;
    const asNum = Number(raw);
    return Number.isFinite(asNum) ? asNum : null;
  }, [currentSalon, user?.salon_id]);

  const salonTimezone = useMemo(() => getSalonTimezone(currentSalon), [currentSalon]);
  const todayIso = useMemo(() => formatDateISO(new Date(), salonTimezone), [salonTimezone]);
  const defaultStartIso = useMemo(
    () => formatDateISO(addDays(new Date(), -29), salonTimezone),
    [salonTimezone]
  );

  const [startDate, setStartDate] = useState(defaultStartIso);
  const [endDate, setEndDate] = useState(todayIso);
  const [groupBy, setGroupBy] = useState("day");
  const [varianceThreshold, setVarianceThreshold] = useState("20");
  const [paidIoMultiplier, setPaidIoMultiplier] = useState("2.5");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const hasAutoRunRef = useRef(false);

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    navigate(getReportsHomePathForRoute(location.pathname), { replace: true });
  };

  const openDailyClosingForDate = useCallback(
    (workDate) => {
      if (!workDate) return;
      const dailyClosingPath = getReportPathForRoute(
        location.pathname,
        "/manager/reports/daily-closing"
      );
      navigate(`${dailyClosingPath}?work_date=${workDate}`, {
        state: { preselectedWorkDateISO: workDate },
      });
    },
    [location.pathname, navigate]
  );
  const renderFieldLabel = (label, tooltipKey) => (
    <div className="flex items-center gap-1.5">
      <label className={LABEL_TEXT}>{label}</label>
      {tooltipKey ? (
        <InfoTooltip text={GIFT_CARDS_CASH_DRAWER_TOOLTIPS[tooltipKey]} label={label} />
      ) : null}
    </div>
  );

  const normalizedGroupBy = VALID_GROUP_BY.has(groupBy) ? groupBy : "day";
  const varianceValue = Math.max(Number(varianceThreshold) || 0, 0);
  const multiplierValue = Math.max(Number(paidIoMultiplier) || 0, 0);

  const payload = useMemo(
    () => ({
      start_date: startDate,
      end_date: endDate,
      group_by: normalizedGroupBy,
      variance_flag_threshold: varianceValue,
      paid_io_spike_multiplier: multiplierValue,
    }),
    [startDate, endDate, normalizedGroupBy, varianceValue, multiplierValue]
  );

  const runReport = useCallback(
    async (overridePayload = null) => {
      if (authLoading) return;
      if (!resolvedSalonId) {
        setError(
          "Unable to determine your current salon. Please refresh or re-select your salon."
        );
        return;
      }
      if (!startDate || !endDate) {
        setError("Start date and end date are required.");
        return;
      }
      if (endDate < startDate) {
        setError("End date cannot be before start date.");
        return;
      }
      const requestPayload = overridePayload || payload;
      setLoading(true);
      setError("");
      try {
        const data = await getGiftCardsCashDrawerReport(requestPayload);
        setReport(data || null);
      } catch (err) {
        setReport(null);
        setError(
          err?.response?.data?.detail || "Unable to load the Cash Drawer Analysis report."
        );
      } finally {
        setLoading(false);
      }
    },
    [authLoading, resolvedSalonId, startDate, endDate, payload]
  );

  useEffect(() => {
    setStartDate(defaultStartIso);
    setEndDate(todayIso);
    hasAutoRunRef.current = false;
  }, [defaultStartIso, todayIso, resolvedSalonId]);

  useEffect(() => {
    if (authLoading || !resolvedSalonId || hasAutoRunRef.current) return;
    hasAutoRunRef.current = true;
    runReport(payload);
  }, [authLoading, resolvedSalonId, runReport, payload]);

  useEffect(() => {
    if (authLoading) return;
    if (MANAGER_ROLES.includes(role) || user?.is_super_admin) return;
    navigate("/my-bookings", { replace: true });
  }, [authLoading, role, navigate, user?.is_super_admin]);

  useEffect(() => {
    if (authLoading || resolvedSalonId) return;
    setError("Select a salon to view reports.");
    const timeout = setTimeout(() => {
      navigate("/my-salons", { replace: true });
    }, 600);
    return () => clearTimeout(timeout);
  }, [authLoading, resolvedSalonId, navigate]);

  const handleExport = useCallback(async () => {
    if (!startDate || !endDate) {
      setError("Start date and end date are required.");
      return;
    }
    if (endDate < startDate) {
      setError("End date cannot be before start date.");
      return;
    }
    try {
      await exportGiftCardsCashDrawerReport(payload);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to export report ZIP.");
    }
  }, [startDate, endDate, payload]);

  const summary = report?.summary ?? {};
  const cashSummary = summary?.cash_drawer ?? {};
  const paidSummary = summary?.paid_in_out ?? {};

  const anomalyEntries = useMemo(
    () => {
      const cashFlags = (report?.anomalies?.cash_over_short?.flagged_days ?? []).map((entry) => ({
        work_date: entry?.work_date,
        reason: entry?.reason || "cash_over_short_variance",
        title: REASON_LABELS[entry?.reason] || "Cash over / short variance",
        detail: `Over / short ${formatMoney(entry?.over_short)} vs threshold ${formatMoney(
          entry?.threshold
        )}`,
      }));
      const paidInFlags = (report?.anomalies?.paid_in_out?.paid_in_spikes ?? []).map((entry) => ({
        work_date: entry?.work_date,
        reason: entry?.reason || "paid_in_spike",
        title: REASON_LABELS[entry?.reason] || "Paid-in spike",
        detail: `Paid in ${formatMoney(entry?.value)} vs baseline ${formatMoney(
          entry?.baseline
        )} x ${formatNumber(entry?.multiplier, 1)}`,
      }));
      const paidOutFlags = (report?.anomalies?.paid_in_out?.paid_out_spikes ?? []).map((entry) => ({
        work_date: entry?.work_date,
        reason: entry?.reason || "paid_out_spike",
        title: REASON_LABELS[entry?.reason] || "Paid-out spike",
        detail: `Paid out ${formatMoney(entry?.value)} vs baseline ${formatMoney(
          entry?.baseline
        )} x ${formatNumber(entry?.multiplier, 1)}`,
      }));

      return [...cashFlags, ...paidInFlags, ...paidOutFlags].sort((a, b) =>
        String(b.work_date || "").localeCompare(String(a.work_date || ""))
      );
    },
    [report]
  );

  const cashTiles = useMemo(
    () => [
      { label: "Expected Cash", value: formatMoney(cashSummary?.total_expected_cash) },
      { label: "Counted Cash", value: formatMoney(cashSummary?.total_counted_cash) },
      { label: "Over / Short", value: formatMoney(cashSummary?.total_over_short) },
      { label: "Flagged Days", value: formatNumber(cashSummary?.flagged_days, 0) },
    ],
    [
      cashSummary?.total_expected_cash,
      cashSummary?.total_counted_cash,
      cashSummary?.total_over_short,
      cashSummary?.flagged_days,
    ]
  );

  const paidTiles = useMemo(
    () => [
      { label: "Paid In", value: formatMoney(paidSummary?.paid_in_total) },
      { label: "Paid Out", value: formatMoney(paidSummary?.paid_out_total) },
      {
        label: "Paid In Flags",
        value: formatNumber(paidSummary?.paid_in_flagged_days, 0),
      },
      {
        label: "Paid Out Flags",
        value: formatNumber(paidSummary?.paid_out_flagged_days, 0),
      },
    ],
    [
      paidSummary?.paid_in_total,
      paidSummary?.paid_out_total,
      paidSummary?.paid_in_flagged_days,
      paidSummary?.paid_out_flagged_days,
    ]
  );

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4 relative">
            <div className="text-xl md:text-2xl font-semibold text-white">
              Cash Drawer Analysis
            </div>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <div
              id="gift-cards-cash-drawer-overview"
              className={getSectionClassName("gift-cards-cash-drawer-overview")}
            >
              <ReportHelpCard
                title={helpGuide.title}
                subtitle={helpGuide.subtitle}
                sections={helpGuide.sections}
                troubleshooting={helpGuide.troubleshooting}
                storageKey="report_help_gift_cards_cash_drawer"
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                {error}
              </div>
            )}

            <div
              id="gift-cards-cash-drawer-filters"
              className={`rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4 overflow-visible ${getSectionClassName(
                "gift-cards-cash-drawer-filters"
              )}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
                <div>
                  {renderFieldLabel("Date From", "start_date")}
                  <input
                    id="gift-cash-start-date"
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("Date To", "end_date")}
                  <input
                    id="gift-cash-end-date"
                    type="date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("Group By", "group_by")}
                  <select
                    id="gift-cash-group-by"
                    value={normalizedGroupBy}
                    onChange={(event) => setGroupBy(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  >
                    <option value="day">Day</option>
                    <option value="week">Week</option>
                  </select>
                </div>
                <div>
                  {renderFieldLabel("Variance Flag Threshold", "variance_flag_threshold")}
                  <input
                    id="gift-cash-variance-threshold"
                    type="number"
                    min="0"
                    step="0.5"
                    value={varianceThreshold}
                    onChange={(event) => setVarianceThreshold(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("Paid In/Out Spike Multiplier", "paid_io_spike_multiplier")}
                  <input
                    id="gift-cash-paid-io-multiplier"
                    type="number"
                    min="0"
                    step="0.1"
                    value={paidIoMultiplier}
                    onChange={(event) => setPaidIoMultiplier(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
              </div>

              {startDate && endDate && endDate < startDate && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                  End date cannot be before start date.
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3">
                <div className="text-xs text-slate-500 flex items-center gap-1.5 overflow-visible">
                  <span>Salon timezone: {report?.tz_name || salonTimezone}</span>
                  <InfoTooltip
                    text={GIFT_CARDS_CASH_DRAWER_TOOLTIPS.salon_timezone}
                    label="Salon timezone"
                  />
                </div>
                <div className="ml-auto flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => runReport(payload)}
                    className={`${BTN_PRIMARY} min-w-[140px]`}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "Run Report"}
                  </button>
                  <button
                    id="gift-cards-cash-drawer-export"
                    type="button"
                    onClick={handleExport}
                    className={`${BTN_OUTLINE} min-w-[140px] ${getSectionClassName(
                      "gift-cards-cash-drawer-export"
                    )}`}
                    disabled={loading}
                  >
                    Export ZIP
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      navigate(getReportsHomePathForRoute(location.pathname))
                    }
                    className={`${BTN_OUTLINE} min-w-[140px]`}
                  >
                    Back to Reports
                  </button>
                </div>
              </div>
            </div>

            <section
              id="gift-cards-cash-drawer-details"
              className={`space-y-4 ${getSectionClassName("gift-cards-cash-drawer-details")}`}
            >
              <div className="text-base md:text-lg font-semibold text-slate-900">
                Cash Over / Short Summary
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                {cashTiles.map((tile) => (
                  <div
                    key={tile.label}
                    className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                  >
                    <div className="text-xs uppercase tracking-wide text-slate-500">
                      {tile.label}
                    </div>
                    <div className="mt-2 text-xl font-semibold text-slate-900">
                      {tile.value}
                    </div>
                  </div>
                ))}
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                Only flagged exceptions are listed at the bottom of this report.
              </div>
            </section>

            <section className="space-y-4">
              <div className="text-base md:text-lg font-semibold text-slate-900">
                Paid In / Paid Out Summary
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                {paidTiles.map((tile) => (
                  <div
                    key={tile.label}
                    className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                  >
                    <div className="text-xs uppercase tracking-wide text-slate-500">
                      {tile.label}
                    </div>
                    <div className="mt-2 text-xl font-semibold text-slate-900">
                      {tile.value}
                    </div>
                  </div>
                ))}
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                Daily paid-in and paid-out rows are hidden. Only spikes are listed in the
                flagged results section.
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-3">
              <div className="text-base md:text-lg font-semibold text-slate-900">
                Flagged Results
              </div>
              {anomalyEntries.length ? (
                <div className="space-y-2">
                  {anomalyEntries.map((entry, index) => (
                    <button
                      type="button"
                      key={`${entry.work_date}-${entry.reason}-${index}`}
                      onClick={() => openDailyClosingForDate(entry.work_date)}
                      className="w-full rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-left text-sm md:text-base text-amber-800 hover:border-amber-300 hover:bg-amber-100"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="font-semibold text-slate-900">
                            {formatDisplayDate(entry.work_date)}
                          </div>
                          <div className="mt-1 font-medium text-amber-900">{entry.title}</div>
                          <div className="text-amber-800">{entry.detail}</div>
                        </div>
                        <span className="inline-flex items-center rounded-full border border-amber-300 bg-white px-3 py-1 text-xs font-semibold text-amber-900">
                          Open Day
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-sm md:text-base text-slate-600">
                  {loading ? "Scanning for anomalies..." : "No anomalies flagged for this range."}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
